// apps/share-web/src/player-view/fullscreen-slot.ts
//
// Слот полноэкранного слоя: где он стоит и что он делает со страницей под собой.
//
// ПОЧЕМУ body, а не регион плеера. Пункт приёмки A1-1 — «полный экран должен
// быть полным экраном». Слот жил в `#share-player-panel`, и слой накрывал только
// регион плеера: шапка Band Way и рельса сетлиста оставались видны по краям, а на
// телефоне шапка съедала ту самую вертикаль, которой чтению со стойки и не
// хватает. Поднять z-index было НЕЛЬЗЯ: `#share-console` — это `.app > *`, а
// layout.css даёт им `position: relative; z-index: 1`, то есть контекст
// наложения. Любой z-index внутри него прижат к уровню 1, и шапка плеера
// (`body[data-pv] #share-topbar { z-index: 30 }`) рисуется поверх слоя, каким бы
// большим его собственный z-index ни был. Из body вопрос порядка слоёв не
// возникает вовсе: `.app` контекста наложения не создаёт (position: relative без
// z-index), так что `.cw-fs` с z-index 90 честно встаёт выше рельсы (50), её
// подложки (40) и шапки (30) и ниже модалок (100).
//
// ПОЧЕМУ inert СОСЕДЯМ, а не класс микшеру. `aria-modal` на слое ничего не
// убирает — это обещание, а не действие: скринридер верит ему только там, где
// сам слой лежит в дереве выше остального, а у нас под слоем остаются шапка,
// рельса и подложка рельсы. Пока слой сидел в регионе плеера, «убрать микшер»
// было тем же самым, что «убрать всё под слоем»; теперь это разные вещи, и
// пометить надо КАЖДОГО соседа слота по body. `inert` снимает и обход Tab, и
// объявление; `aria-hidden` идёт рядом — на браузерах без inert (Safari < 15.5)
// он остаётся единственным, что работает.
//
// Прежние значения запоминаются и возвращаются, а не стираются: `.rail-backdrop`
// или декоративный узел могли быть спрятаны ДО нас, и снять чужое aria-hidden
// значит объявить скринридеру декорацию содержимым.

/**
 * Теги, которые не рисуются вовсе. Фокус на них не встаёт и скринридер их не
 * объявляет, так что `inert` им ничего не меняет — а список закрытый, поэтому
 * пропуск честнее, чем пометки на `<script>`. Замер живьём на девфикстуре:
 * `body` держит `#app`, `<script>`, `.rail-backdrop` и фоновый `<audio>` —
 * `<audio>` в списке НЕТ намеренно: с `controls` он фокусируемый.
 */
const NOT_RENDERED = new Set(['SCRIPT', 'STYLE', 'LINK', 'META', 'TITLE', 'TEMPLATE', 'NOSCRIPT']);

/** Что было на узле до того, как слой его накрыл. */
interface Prior {
  el: HTMLElement;
  inert: boolean;
  ariaHidden: string | null;
}

export interface FullscreenSlot {
  /** Хост слоя. Пустой — снят `:empty` в chart.css, клика не ловит. */
  readonly el: HTMLElement;
  /** Страница под слоем уходит из обхода Tab и из дерева доступности. */
  cover(): void;
  /** …и возвращается ровно в то состояние, в котором была. */
  uncover(): void;
  /** Снимает слот и отпускает страницу. Обязателен: слот в body переживёт
   *  хост плеера, потому что `host.textContent = ''` до него не достаёт. */
  destroy(): void;
}

export function createFullscreenSlot(): FullscreenSlot {
  const el = document.createElement('div');
  el.className = 'pv-chart-layer';
  document.body.appendChild(el);

  let covered: Prior[] | null = null;

  const cover = (): void => {
    // Повторный вызов не размножает пометки и, что важнее, не перезаписывает
    // запомненное «до» уже накрытым состоянием.
    if (covered) return;
    const prior: Prior[] = [];
    for (const child of [...document.body.children]) {
      if (child === el) continue;
      const node = child as HTMLElement;
      if (NOT_RENDERED.has(node.tagName)) continue;
      prior.push({
        el: node,
        inert: node.hasAttribute('inert'),
        ariaHidden: node.getAttribute('aria-hidden'),
      });
      node.setAttribute('inert', '');
      node.setAttribute('aria-hidden', 'true');
    }
    covered = prior;
  };

  const uncover = (): void => {
    if (!covered) return;
    for (const p of covered) {
      if (!p.inert) p.el.removeAttribute('inert');
      if (p.ariaHidden == null) p.el.removeAttribute('aria-hidden');
      else p.el.setAttribute('aria-hidden', p.ariaHidden);
    }
    covered = null;
  };

  return {
    el,
    cover,
    uncover,
    destroy() {
      uncover();
      el.remove();
    },
  };
}
